import { useCallback, useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { SelectInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { api, ApiError } from "@/lib/apiClient";
import { AUSWERTUNGEN_SECTION_SUBTITLES } from "@/lib/auswertungenNav";
import "./auswertungen.css";

type Summary = {
  days: number;
  customer_logins: number;
  active_customers: number;
  unknown_attempts: number;
  unknown_emails: number;
  verify_failures: number;
  customer_otp_requests: number;
};

type CustomerRow = {
  email: string;
  display_name?: string | null;
  firmennamen?: string | null;
  login_count: number;
  otp_request_count: number;
  last_login_at?: string | null;
};

type UnknownRow = {
  email: string;
  attempt_count: number;
  last_attempt_at?: string | null;
  last_ip?: string | null;
};

const DAY_OPTIONS = [
  { value: "30", label: "30 Tage" },
  { value: "90", label: "90 Tage" },
  { value: "180", label: "180 Tage" },
  { value: "365", label: "365 Tage" },
];

function formatDateTime(value?: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("de-DE");
}

export function PortalUsagePage() {
  const [days, setDays] = useState("90");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [unused, setUnused] = useState<CustomerRow[]>([]);
  const [unknown, setUnknown] = useState<UnknownRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = Number(days) || 90;
      const [sum, cust, unk] = await Promise.all([
        api.get<Summary>(`/reports/portal-usage/summary?days=${d}`),
        api.get<{ items: CustomerRow[]; unused_contacts: CustomerRow[] }>(
          `/reports/portal-usage/customers?days=${d}&include_zero=true&limit=300`,
        ),
        api.get<{ items: UnknownRow[] }>(
          `/reports/portal-usage/unknown?days=${d}&limit=300`,
        ),
      ]);
      setSummary(sum);
      setCustomers(cust.items || []);
      setUnused(cust.unused_contacts || []);
      setUnknown(unk.items || []);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Portal-Nutzung konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AppShell
      title="Auswertungen"
      subtitle={AUSWERTUNGEN_SECTION_SUBTITLES["portal-usage"]}
    >
      <div className="eh-stack" style={{ gap: "1.25rem" }}>
        <div className="eh-row" style={{ gap: "0.75rem", alignItems: "end", flexWrap: "wrap" }}>
          <SelectInput
            label="Zeitraum"
            value={days}
            onChange={(e) => setDays(e.target.value)}
          >
            {DAY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectInput>
          <Button type="button" onClick={() => void load()} disabled={loading}>
            Aktualisieren
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {loading && !summary && (
          <div className="eh-row" style={{ gap: "0.5rem", alignItems: "center" }}>
            <Spinner /> <span>Wird geladen …</span>
          </div>
        )}

        {summary && (
          <div
            className="eh-row"
            style={{ gap: "0.75rem", flexWrap: "wrap" }}
          >
            {[
              ["Logins", summary.customer_logins],
              ["Aktive Kunden", summary.active_customers],
              ["OTP-Anfragen", summary.customer_otp_requests],
              ["Unbekannte Versuche", summary.unknown_attempts],
              ["Unbekannte E-Mails", summary.unknown_emails],
              ["Fehlversuche Code", summary.verify_failures],
            ].map(([label, value]) => (
              <div
                key={String(label)}
                style={{
                  minWidth: "8.5rem",
                  border: "1px solid var(--eh-separator-opaque)",
                  padding: "0.75rem 1rem",
                  background: "var(--eh-bg-card, #fff)",
                }}
              >
                <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>{label}</div>
                <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        <section>
          <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem" }}>
            Häufigste Nutzer
          </h2>
          <p style={{ margin: "0 0 0.75rem", opacity: 0.75, fontSize: "0.9rem" }}>
            Kunden mit erfolgreichen Portal-Logins im Zeitraum, absteigend nach
            Häufigkeit.
          </p>
          {customers.length === 0 ? (
            <p style={{ opacity: 0.7 }}>Noch keine Kunden-Logins im Zeitraum.</p>
          ) : (
            <div className="eh-table-wrap">
              <table className="eh-table">
                <thead>
                  <tr>
                    <th>Kunde</th>
                    <th>E-Mail</th>
                    <th>Logins</th>
                    <th>OTP-Anfragen</th>
                    <th>Letzter Login</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((row) => (
                    <tr key={row.email}>
                      <td>
                        {row.display_name || "–"}
                        {row.firmennamen ? (
                          <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>
                            {row.firmennamen}
                          </div>
                        ) : null}
                      </td>
                      <td>{row.email}</td>
                      <td>{row.login_count}</td>
                      <td>{row.otp_request_count}</td>
                      <td>{formatDateTime(row.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem" }}>
            Ohne Zugang – Anmeldeversuche
          </h2>
          <p style={{ margin: "0 0 0.75rem", opacity: 0.75, fontSize: "0.9rem" }}>
            E-Mail-Adressen, die nicht als Kontakt hinterlegt sind, aber einen
            Anmeldecode angefordert haben.
          </p>
          {unknown.length === 0 ? (
            <p style={{ opacity: 0.7 }}>Keine unbekannten Anmeldeversuche.</p>
          ) : (
            <div className="eh-table-wrap">
              <table className="eh-table">
                <thead>
                  <tr>
                    <th>E-Mail</th>
                    <th>Versuche</th>
                    <th>Zuletzt</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {unknown.map((row) => (
                    <tr key={row.email}>
                      <td>{row.email}</td>
                      <td>{row.attempt_count}</td>
                      <td>{formatDateTime(row.last_attempt_at)}</td>
                      <td>{row.last_ip || "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem" }}>
            Kontakte ohne Portal-Nutzung
          </h2>
          <p style={{ margin: "0 0 0.75rem", opacity: 0.75, fontSize: "0.9rem" }}>
            Hinterlegte Kontakt-Mails ohne Login oder OTP-Anfrage im Zeitraum
            (Auszug).
          </p>
          {unused.length === 0 ? (
            <p style={{ opacity: 0.7 }}>
              Alle gefundenen Kontakte hatten im Zeitraum Portal-Aktivität – oder
              es gibt keine weiteren Kontakte.
            </p>
          ) : (
            <div className="eh-table-wrap">
              <table className="eh-table">
                <thead>
                  <tr>
                    <th>Kunde</th>
                    <th>E-Mail</th>
                  </tr>
                </thead>
                <tbody>
                  {unused.slice(0, 100).map((row) => (
                    <tr key={row.email}>
                      <td>
                        {row.display_name || "–"}
                        {row.firmennamen ? (
                          <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>
                            {row.firmennamen}
                          </div>
                        ) : null}
                      </td>
                      <td>{row.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {unused.length > 100 && (
                <p style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                  … und {unused.length - 100} weitere
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}
